import ArrowLeftThin from '@assets/icons/arrow-left-thin.svg';
import CartIcon from '@assets/icons/cart.svg';
import i18n from '@/utils/i18n';
import React, { useMemo, useRef, useState } from 'react';
import SalesPortalIcon from '@assets/icons/sales-portal.svg';
import {
  AllowedShortcuts,
  KeyboardScope,
} from '@/platform/shortcuts/operator-shortcuts';
import { AnalyticEvent } from '@/analytics/fennel/registry';
import { Analytics } from '@/utils/analytics';
import {
  APP_CONTAINER_ROUTES,
  CATALOG_ROUTES,
  CONTAINER_MODAL_ROUTES,
  ORDER_GUIDE_ROUTES,
  ORDER_INBOUNDS_ROUTES,
  SALES_PORTAL_ROUTES,
} from '@/navigators/routes';
import { AppContainerStackParams } from '@/navigators/types';
import { Button } from '@/components/button';
import { ChatStatus, PermissionAction, PermissionSubject } from '@/enums';
import { createStyles } from '@/contexts/theme-context';
import { DRAWER_HEADER_HEIGHT, shadow, TYPOGRAPHY } from '@/styles';
import { ExperienceMode, useExperienceStore } from '@/stores/experience-store';
import { fennelTrack } from '@/analytics/fennel/hook';
import { I2HeaderButton } from './i2-header-button';
import { InsightsHubIcon } from '@/components/locations/insights-hub-icon';
import { LocationPickerActionSheet } from '@/components/locations/location-picker-action-sheet';
import { OfflineDownloadProgressBar } from '@/components/offline-download-progress-bar';
import { OfflineModeBanner } from '@/components/offline-mode-banner';
import { PerfTiming, PerfTimingEvent } from '@/utils/perf-timing';
import {
  Platform,
  StyleProp,
  Text,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { QueuedOfflineOrdersButton } from '@/components/offline-orders/queued-offline-orders-button';
import { RestaurantSelectorButton } from '@/components/restaurant-selector-button';
import { SalesRepButton } from '@/components/locations/sales-rep-button';
import { SharePriceQuoteDropDownButton } from '@/components/partners/share-price-quote-button';
import {
  shouldShowOrder,
  useNewOrderActionsContext,
} from '@/contexts/new-order-actions-context';
import { StackNavigationProp } from '@react-navigation/stack';
import { TrainingModeSwitch } from './training-mode-switch';
import {
  useActiveCartName,
  useOrderButtonText,
  useShowCartButton,
} from '@/hooks/use-order-button-text';
import {
  useAuthenticatedUserContext,
  useUserSettingsContext,
} from '@/contexts/user-context';
import { useChatsContext } from '@/contexts/chats-context';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useKeyboardShortcuts } from '@platform/shortcuts/hooks/use-keyboard-shortcuts';
import { useMarkHubAsViewed } from '@/hooks/insights-hub/use-mark-hub-as-viewed';
import { useMaybeOrderDrawerContext } from '@/contexts/order-drawer-context';
import { useNavigation, useNavigationState } from '@react-navigation/native';
import { useNavigationConfirmationContext } from '@/contexts/navigation-confirmation-context';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useOfflineModeEnabled } from '@/hooks/use-offline-mode-enabled';
import { useOfflineQueuedOrders } from '@/hooks/use-offline-data';
import { useOrderInbounds } from '@/hooks/use-order-inbounds';
import { usePaymentsContext } from '@/contexts/payments-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSalesPortalNavigationStore } from '@/experiences/sales-portal/blocs/stores/sales-portal-navigation-store';
import { useSalesRepMode } from '@/hooks/use-sales-rep-mode';
import { useToasts } from '@/contexts/toast-context';

type Navigation = StackNavigationProp<AppContainerStackParams>;

interface Props {
  DrawerLeftComponent: React.ReactNode;
  hideShadow?: boolean;
  inInboundsStack?: boolean;
  leftComponentStyle?: StyleProp<ViewStyle>;
}

const ACCOUNT_HOLD_CONTAINER_WIDTH = 208;
const SALES_REP_BUTTON_HEIGHT = 25 / 1.5;
const SALES_REP_BUTTON_WIDTH = 22 / 1.5;

export const NAVIGABLE_PAGES: string[] = [
  APP_CONTAINER_ROUTES.InsightsHub,
  APP_CONTAINER_ROUTES.StorefrontMicroProduct,
];

export const DrawerHeader: React.FC<Props> = ({
  DrawerLeftComponent,
  leftComponentStyle,
  inInboundsStack,
  hideShadow,
}) => {
  const { switchToExperience } = useExperienceStore();
  const navigationState = useNavigationState((state) => state.routes[0].state);
  const orderDrawerContext = useMaybeOrderDrawerContext();
  const { updateAndClearQuantity } = useNewOrderActionsContext();
  const { draftOrder, hasResponse, multiCartEnabled } = useNewOrderContext();
  const activeCartName = useActiveCartName();
  const { chatUnreadCountMap } = useChatsContext();
  const { restaurantsWithNewInvoices } = usePaymentsContext();
  const insightsHubConfig = useDynamicConfigValue('insights_hub_config');
  const salesPortalConfig = useDynamicConfigValue('sales_portal');
  const { markHubViewed } = useMarkHubAsViewed();
  const { showErrorToast } = useToasts();
  const { top } = useSafeAreaInsets();
  const { navigate, goBack } = useNavigation<Navigation>();
  const { navigationBlocked } = useNavigationConfirmationContext();
  const {
    employeeCan,
    selectedRestaurantRelationship,
    selectedRestaurantRelationship: { onHold: isRestaurantOnHold },
    supplierEmployeeRole,
  } = useAuthenticatedUserContext();
  const { trainingModeEnabled, isOrderInboundsEnabled } = useOrderInbounds();
  const { setShowSalesRepUI } = useUserSettingsContext();
  const [styles, theme] = useStyles();
  const buttonText = useOrderButtonText();
  const showCartButton = useShowCartButton();
  const cartNameSettled = !multiCartEnabled || hasResponse;
  const salesRepMode = useSalesRepMode();

  const locationPickerTargetRef = useRef<View>(null);
  const [loading, setLoading] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const showSalesRepButton =
    employeeCan(PermissionAction.Read, PermissionSubject.SrtPriceEditing) &&
    selectedRestaurantRelationship.salesRepModeEnabled;
  const { offlineRestaurantWithQueuedOrders } = useOfflineQueuedOrders();
  const isOfflineModeEnabled = useOfflineModeEnabled();

  const onPressOrderButton = async () => {
    if (!orderDrawerContext) {
      return;
    }
    PerfTiming.start(PerfTimingEvent.ORDER_SUMMARY_VISIBLE, {
      entryPoint: 'drawer_header_order_button',
    });
    setLoading(true);
    try {
      const result = await updateAndClearQuantity('drawer_header');
      if (shouldShowOrder(result)) {
        orderDrawerContext.openDrawer();
      }
    } catch (e) {
      showErrorToast();
    }
    setLoading(false);
  };

  const onI2HeaderButtonPress = () => {
    navigate(CONTAINER_MODAL_ROUTES.OrderInbounds, {
      screen: ORDER_INBOUNDS_ROUTES.List,
    });
  };

  useKeyboardShortcuts(
    AllowedShortcuts.ToggleLocationPicker,
    () => {
      setShowLocationPicker(!showLocationPicker);
    },
    [showLocationPicker],
    {},
    true,
    KeyboardScope.GLOBAL,
  );

  const isInNavigablePages = useMemo(
    () =>
      Boolean(
        navigationState?.routes.find((route) =>
          NAVIGABLE_PAGES.includes(route.name),
        ),
      ),
    [navigationState],
  );

  useKeyboardShortcuts(
    AllowedShortcuts.ToggleSalesRepMode,
    () => {
      setShowSalesRepUI(!salesRepMode);
    },
    [salesRepMode],
    {
      chatUUID: selectedRestaurantRelationship.uuid,
      salesRepMode,
      supplierUUID: selectedRestaurantRelationship.supplierUUID,
    },
  );

  useKeyboardShortcuts(
    AllowedShortcuts.ToggleCart,
    async () => {
      if (!orderDrawerContext) {
        return;
      }
      if (orderDrawerContext.open) {
        orderDrawerContext.closeDrawer();
      } else {
        await onPressOrderButton();
      }
    },
    [orderDrawerContext, onPressOrderButton],
    {},
    true,
    KeyboardScope.GLOBAL,
  );

  const isSupplierEmployee = Boolean(supplierEmployeeRole);

  const isSalesPortalSwitchEnabled =
    isSupplierEmployee && !!salesPortalConfig?.enabled && !inInboundsStack;

  useKeyboardShortcuts(
    AllowedShortcuts.SwitchExperience,
    () => {
      // this component only shows the button to switch to Sales Portal,
      // so we directly switch to that experience
      useSalesPortalNavigationStore
        .getState()
        .setLastActiveRoute(SALES_PORTAL_ROUTES.Home);
      switchToExperience(ExperienceMode.SALES_PORTAL);
    },
    [switchToExperience],
    {},
    isSalesPortalSwitchEnabled,
    KeyboardScope.GLOBAL,
  );

  /**
   * Helper function to get the container style
   * depending on the section and the current enabled mode of the user.
   * @returns {StyleProp<ViewStyle>}
   */
  const getContainerStyle = () => {
    const sectionStyle = isInNavigablePages
      ? { ...styles.headerInsightsHub, ...styles.headerShadowReset }
      : salesRepMode
        ? styles.headerDark
        : inInboundsStack && trainingModeEnabled
          ? styles.headerTraining
          : null;
    return [
      styles.header,
      hideShadow ? styles.headerBorder : styles.headerShadow,
      sectionStyle,
      { marginTop: top },
    ];
  };

  const isInTagPrintBatchScreen = useMemo(() => {
    return navigationState?.routes.some(
      ({ name }) =>
        name === ORDER_GUIDE_ROUTES.TagPrintBatch ||
        name === CATALOG_ROUTES.TagPrintBatch,
    );
  }, [navigationState]);

  return (
    <>
      <View style={getContainerStyle()}>
        <View
          style={[
            styles.leftComponent,
            leftComponentStyle,
            salesRepMode ? styles.borderRight : styles.borderRightLight,
          ]}
        >
          {DrawerLeftComponent}
        </View>
        <View style={styles.buttonContainer}>
          <View style={[styles.buttonContainerRow, styles.leadingRow]}>
            {isInNavigablePages ? (
              <TouchableOpacity
                accessibilityLabel={i18n.t('back_to', {
                  name: selectedRestaurantRelationship.name,
                })}
                onPress={goBack}
                style={styles.headerButton}
              >
                <ArrowLeftThin fill={theme.sk.color.icon.primary} />
                <Text style={[TYPOGRAPHY.pBold, styles.insightsHubLabel]}>
                  {i18n.t('back_to', {
                    name: selectedRestaurantRelationship.name,
                  })}
                </Text>
              </TouchableOpacity>
            ) : (
              <>
                {!inInboundsStack ? (
                  <RestaurantSelectorButton
                    onPress={() => setShowLocationPicker(true)}
                    style={[
                      styles.locationPicker,
                      salesRepMode
                        ? styles.borderRight
                        : styles.borderRightLight,
                    ]}
                    viewRef={locationPickerTargetRef}
                  />
                ) : null}
                {showLocationPicker ? (
                  <LocationPickerActionSheet
                    chatUnreadCountMap={chatUnreadCountMap}
                    onClose={() => setShowLocationPicker(false)}
                    restaurantsWithNewInvoices={restaurantsWithNewInvoices}
                    targetRef={locationPickerTargetRef}
                  />
                ) : null}

                {isOrderInboundsEnabled && !inInboundsStack ? (
                  <I2HeaderButton
                    containerStyle={[
                      styles.i2HeaderButtonContainer,
                      salesRepMode
                        ? styles.borderRight
                        : styles.borderRightLight,
                    ]}
                    iconColor={
                      salesRepMode
                        ? theme.sk.color.icon.ondark
                        : theme.sk.color.icon.primary
                    }
                    onPress={onI2HeaderButtonPress}
                  />
                ) : null}

                {isSupplierEmployee &&
                insightsHubConfig?.enabled &&
                !inInboundsStack ? (
                  <TouchableOpacity
                    accessibilityLabel={`${i18n.t('insights_hub')} button`}
                    onPress={async () => {
                      // TODO(DSR-2616): open action sheet for first time users
                      navigate(APP_CONTAINER_ROUTES.InsightsHub);
                      Analytics.track('drawer_header.insights_hub_button');
                      void markHubViewed();
                    }}
                    style={[
                      styles.headerButton,
                      salesRepMode
                        ? styles.borderRight
                        : styles.borderRightLight,
                    ]}
                  >
                    <InsightsHubIcon
                      badgeStyle={styles.customBadge}
                      salesRepModeEnabled={salesRepMode}
                    />
                  </TouchableOpacity>
                ) : null}
                {isSalesPortalSwitchEnabled ? (
                  <TouchableOpacity
                    accessibilityLabel={`${i18n.t('sales_portal')} button`}
                    onPress={() => {
                      useSalesPortalNavigationStore
                        .getState()
                        .setLastActiveRoute(SALES_PORTAL_ROUTES.Home);
                      switchToExperience(ExperienceMode.SALES_PORTAL);
                      fennelTrack(AnalyticEvent.PORTAL_SWITCH_BUTTON_PRESS, {
                        platform: Platform.OS,
                        source: ExperienceMode.OPERATOR_PORTAL,
                      });
                    }}
                    style={[
                      styles.headerButton,
                      salesRepMode
                        ? styles.borderRight
                        : styles.borderRightLight,
                    ]}
                  >
                    <SalesPortalIcon
                      stroke={
                        salesRepMode
                          ? theme.sk.color.icon.ondark
                          : theme.sk.color.icon.primary
                      }
                    />
                  </TouchableOpacity>
                ) : null}
                {showSalesRepButton ? (
                  <SalesRepButton
                    height={SALES_REP_BUTTON_HEIGHT}
                    onPress={() => {
                      setShowSalesRepUI(!salesRepMode);
                      Analytics.track('drawer_header.sales_rep_button');
                    }}
                    salesRepModeEnabled={salesRepMode}
                    style={styles.salesRepButton}
                    width={SALES_REP_BUTTON_WIDTH}
                  />
                ) : null}
              </>
            )}
            {isInTagPrintBatchScreen ? null : (
              <OfflineModeBanner
                containerStyle={[
                  styles.offlineModeContainer,
                  isInNavigablePages ? styles.headerInsightsHub : undefined,
                ]}
                textStyle={
                  isInNavigablePages ? styles.offlineModeText : undefined
                }
              />
            )}
          </View>
          <View style={styles.buttonContainerRow}>
            {!isInNavigablePages ? (
              <>
                {offlineRestaurantWithQueuedOrders && isOfflineModeEnabled ? (
                  <View style={styles.queuedOfflineOrdersButtonContainer}>
                    <QueuedOfflineOrdersButton />
                  </View>
                ) : null}
                {isRestaurantOnHold ? (
                  <View style={styles.accountHoldContainer}>
                    <Text style={[TYPOGRAPHY.pBold, styles.accountHoldText]}>
                      {i18n.t('account_on_hold.message')}
                    </Text>
                  </View>
                ) : null}
                {!inInboundsStack ? (
                  selectedRestaurantRelationship.chatStatus ===
                  ChatStatus.PROSPECT ? (
                    // For prospects render the create price list button
                    <SharePriceQuoteDropDownButton />
                  ) : showCartButton && cartNameSettled ? (
                    // For non-prospects, render Order Button
                    <Button
                      accessibilityLabel={
                        activeCartName
                          ? i18n.t('multi_cart.view_cart_label', {
                              name: activeCartName,
                            })
                          : i18n.t('view_cart')
                      }
                      containerStyle={salesRepMode ? styles.borderLeft : null}
                      disabled={navigationBlocked}
                      label={activeCartName}
                      leftElement={<CartIcon fill={theme.colors.white} />}
                      leftElementStyle={
                        activeCartName ? styles.cartButtonIcon : undefined
                      }
                      loading={loading}
                      onPress={onPressOrderButton}
                      style={styles.orderButton}
                      testID="view-order-button-lg"
                      trackingData={{
                        name: 'drawer_header.cart_button',
                        properties: {
                          orderUUID: draftOrder?.uuid,
                        },
                      }}
                    >
                      {buttonText ?? i18n.t('view_cart')}
                    </Button>
                  ) : null
                ) : (
                  <TrainingModeSwitch
                    containerStyle={styles.trainingModeSwitch}
                    salesRepMode={salesRepMode}
                  />
                )}
              </>
            ) : null}
          </View>
        </View>
      </View>
      <OfflineDownloadProgressBar />
    </>
  );
};

const useStyles = createStyles((theme) => ({
  accountHoldContainer: {
    backgroundColor: theme.colors.onHoldHeading,
    justifyContent: 'center',
    right: -1,
    width: ACCOUNT_HOLD_CONTAINER_WIDTH,
  },
  accountHoldText: {
    fontSize: 12,
    lineHeight: 14,
    textAlign: 'center',
  },
  borderLeft: {
    borderLeftColor: theme.sk.color.divider.ondark,
    borderLeftWidth: 1,
  },
  borderRight: {
    borderRightColor: theme.sk.color.divider.ondark,
    borderRightWidth: 1,
  },
  borderRightLight: {
    borderRightColor: theme.sk.color.border.primary,
    borderRightWidth: 1,
  },
  buttonContainer: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  buttonContainerRow: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  cartButtonIcon: { marginRight: 11 },
  customBadge: {
    right: -5,
  },
  header: {
    backgroundColor: theme.colors.white,
    flexDirection: 'row',
    height: DRAWER_HEADER_HEIGHT,
    justifyContent: 'space-between',
  },
  headerBorder: {
    borderBottomColor: theme.sk.color.border.primary,
    borderBottomWidth: 4,
  },
  headerButton: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
    height: '100%',
    justifyContent: 'center',
    paddingHorizontal: 24,
  },
  headerDark: {
    backgroundColor: theme.colors.primary,
  },
  headerInsightsHub: {
    backgroundColor: theme.sk.color.bg.primary.default,
  },
  headerShadow: {
    ...shadow({
      color: theme.colors.black,
      opacity: 0.1,
      radius: 12,
      x: 0,
      y: 4,
    }),
  },
  headerShadowReset: {
    elevation: 0,
    shadowOpacity: 0,
  },
  headerTraining: {
    backgroundColor: theme.sk.color.bg.secondary.default,
  },
  i2HeaderButtonContainer: {
    paddingHorizontal: 24,
    paddingVertical: 20,
  },
  insightsHubLabel: {
    color: theme.colors.secondary,
    fontFamily: theme.fonts.primaryBold,
    fontSize: 14,
  },
  // Prevent long restaurant names from pushing other elements off screen
  leadingRow: {
    flexShrink: 1,
  },
  leftComponent: {
    justifyContent: 'center',
  },
  locationPicker: {
    height: DRAWER_HEADER_HEIGHT,
    justifyContent: 'center',
    paddingHorizontal: 24,
  },
  offlineModeContainer: {
    marginLeft: 24,
  },
  offlineModeText: {
    color: theme.colors.primary,
  },
  orderButton: {
    borderRadius: 0,
    height: DRAWER_HEADER_HEIGHT,
  },
  queuedOfflineOrdersButtonContainer: {
    justifyContent: 'center',
    paddingRight: 24,
  },
  salesRepButton: {
    alignItems: 'center',
    justifyContent: 'center',
    width: DRAWER_HEADER_HEIGHT,
  },
  trainingModeSwitch: {
    marginRight: 24,
  },
}));
